import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Section, Heading } from "../components/Bits";
import PageHero from "../components/PageHero";
import { Check, Copy, ExternalLink, Leaf, CalendarClock, Sparkles } from "lucide-react";

const WIDGETS = [
  {
    key: "plants",
    icon: Leaf,
    title: "Pflanzen-Galerie",
    desc: "Zeigt eine Auswahl von Wildpflanzen mit Bild, Name und Kategorie. Ideal für Blogs, Vereine oder Naturmagazine.",
    path: "/embed/plants",
    height: 620,
    params: [
      { key: "limit", label: "Anzahl Pflanzen", type: "number", default: 9, min: 3, max: 30 },
    ],
  },
  {
    key: "tours",
    icon: CalendarClock,
    title: "Tour-Kalender",
    desc: "Die nächsten geführten Wildkräuter-Touren – live aus unserem Backend. Buchbar direkt über eure Seite.",
    path: "/embed/tours",
    height: 700,
    params: [
      { key: "limit", label: "Anzahl Touren", type: "number", default: 6, min: 1, max: 20 },
      { key: "mode", label: "Modus", type: "select", options: [
        { value: "calendar", label: "Kalender-Ansicht" },
        { value: "book", label: "Buchungs-Ansicht" },
      ], default: "calendar" },
    ],
  },
];

function buildSrc(path, cfg, origin) {
  const params = new URLSearchParams();
  Object.entries(cfg).forEach(([k, v]) => {
    if (v !== "" && v !== undefined && v !== null) params.set(k, String(v));
  });
  const q = params.toString();
  return `${origin}${path}${q ? "?" + q : ""}`;
}

function WidgetCard({ widget, origin }) {
  const Ico = widget.icon;
  const initial = useMemo(() => {
    const out = {};
    widget.params.forEach((p) => { out[p.key] = p.default; });
    return out;
  }, [widget]);
  const [cfg, setCfg] = useState(initial);
  const [copied, setCopied] = useState(false);

  const src = buildSrc(widget.path, cfg, origin);
  const snippet = `<iframe src="${src}"\n  width="100%"\n  height="${widget.height}"\n  style="border:0;border-radius:16px;overflow:hidden;max-width:900px"\n  loading="lazy"\n  title="${widget.title} – wildkräuter.lu">\n</iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  return (
    <div className="plant-card overflow-hidden" data-testid={`partner-widget-${widget.key}`}>
      <div className="p-6 border-b" style={{ borderColor: "var(--wp-border)" }}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full inline-flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>
            <Ico className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">Widget</div>
            <h3 className="font-display text-2xl mb-1" style={{ color: "var(--wp-olive)" }}>{widget.title}</h3>
            <p className="text-sm opacity-80">{widget.desc}</p>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="overline mb-3">Einstellungen</div>
          <div className="space-y-3">
            {widget.params.map((p) => (
              <label key={p.key} className="block text-sm">
                <div className="opacity-70 mb-1">{p.label}</div>
                {p.type === "select" ? (
                  <select
                    value={cfg[p.key]}
                    onChange={(e) => setCfg({ ...cfg, [p.key]: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ borderColor: "var(--wp-border)" }}
                    data-testid={`partner-${widget.key}-${p.key}`}
                  >
                    {p.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={p.type}
                    min={p.min}
                    max={p.max}
                    value={cfg[p.key]}
                    onChange={(e) => setCfg({ ...cfg, [p.key]: p.type === "number" ? (parseInt(e.target.value) || p.default) : e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ borderColor: "var(--wp-border)" }}
                    data-testid={`partner-${widget.key}-${p.key}`}
                  />
                )}
              </label>
            ))}
          </div>

          <div className="overline mt-6 mb-2">HTML-Snippet</div>
          <div className="relative">
            <pre
              className="text-[11px] leading-relaxed p-3 rounded-lg border overflow-x-auto whitespace-pre"
              style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
              data-testid={`partner-snippet-${widget.key}`}
            >{snippet}</pre>
            <button
              onClick={copy}
              className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: copied ? "var(--wp-primary)" : "var(--wp-olive)", color: "var(--wp-cream)" }}
              data-testid={`partner-copy-${widget.key}`}
            >
              {copied ? <><Check className="w-3 h-3" /> Kopiert</> : <><Copy className="w-3 h-3" /> Kopieren</>}
            </button>
          </div>
        </div>

        <div>
          <div className="overline mb-3">Live-Vorschau</div>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--wp-border)" }}>
            <iframe
              src={src}
              title={`Vorschau ${widget.title}`}
              className="w-full block"
              style={{ height: Math.min(widget.height, 520), border: 0 }}
              loading="lazy"
              data-testid={`partner-preview-${widget.key}`}
            />
          </div>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
            style={{ color: "var(--wp-olive)" }}
          >
            <ExternalLink className="w-3 h-3" /> Vorschau in neuem Tab öffnen
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Partner() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <Section>
      <PageHero
        slug="partner"
        defaultImage="/fotos/roter-weg.webp"
        imageAlt="Waldweg mit roter Erde unter blauem Himmel"
        defaultOverline="Für Partner, Presse & Vereine"
        defaultTitle="Bringt Wildpflanzen auf eure Website"
        defaultSubtitle="Kostenlose Widgets mit unserer Pflanzendatenbank und aktuellen Touren – live, mobil-optimiert und in eurem Design integriert."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-5xl">
        <Feature icon={<Leaf className="w-5 h-5" />} title="Kostenlos & für immer">
          Nutzt unsere Widgets ohne versteckte Kosten. Ein kurzer Hinweis auf adventurecouple.lu freut uns.
        </Feature>
        <Feature icon={<Sparkles className="w-5 h-5" />} title="Immer aktuell">
          Neue Pflanzen und Touren erscheinen automatisch – ihr müsst nichts manuell nachpflegen.
        </Feature>
        <Feature icon={<CalendarClock className="w-5 h-5" />} title="Direkt buchbar">
          Besucher können Touren direkt aus dem Widget heraus buchen. Ihr bekommt eure Sichtbarkeit, wir die Anmeldung.
        </Feature>
      </div>

      <Heading overline="Widget-Baukasten" title="Konfigurieren, kopieren, einbetten" subtitle="Wählt Größe & Inhalt, kopiert das HTML-Snippet und fügt es dort ein, wo das Widget erscheinen soll." />

      <div className="space-y-8 mb-12">
        {WIDGETS.map((w) => (
          <WidgetCard key={w.key} widget={w} origin={origin} />
        ))}
      </div>

      <div className="plant-card p-6 sm:p-8 max-w-4xl" data-testid="partner-contact-cta">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="overline mb-1">Ihr wollt mehr?</div>
            <h3 className="font-display text-2xl mb-1" style={{ color: "var(--wp-olive)" }}>Individuelle Kooperation</h3>
            <p className="text-sm opacity-80">
              Eigenes Farbdesign, exklusive Tour für eure Community, White-Label-Widget oder Presse-Anfrage? Meldet euch – wir freuen uns auf einen persönlichen Austausch.
            </p>
          </div>
          <Link to="/contact" className="btn-primary justify-center" data-testid="partner-contact-btn">
            Kontakt aufnehmen
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="plant-card p-5">
      <div className="w-10 h-10 rounded-full inline-flex items-center justify-center mb-3" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>
        {icon}
      </div>
      <div className="font-medium mb-1" style={{ color: "var(--wp-olive)" }}>{title}</div>
      <p className="text-xs opacity-70 leading-relaxed">{children}</p>
    </div>
  );
}
